import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, User, LogOut, Compass, SlidersHorizontal } from 'lucide-react'
import { preferencesPathFor } from '../lib/landing'
import { AccountAvatar } from './AccountAvatar'
import { startProductTour } from './ProductTour/useProductTour'

function roleLabel(role) {
  if (role === 'super_admin') return 'Master Admin'
  if (role === 'company_admin') return 'Company Admin'
  if (role === 'sub_admin') return 'Sub Admin'
  return 'User'
}

/** Show the work-role under the panel-role for plain users so the topbar
 *  pill reflects their assigned Work Role. Work Roles are operations-driven,
 *  so the assigned role's actual catalogue NAME (e.g. a custom "Manager") is
 *  what we show — the legacy `workRole` tier slug is inert and only used as a
 *  fallback label when no Work Role is assigned. Platform admins and company
 *  admins have no work-role concept. */
function displayRoleLabel(user) {
  if (user.role !== 'user') return roleLabel(user.role)
  if (user.workRoleName) return user.workRoleName
  const wr = user.workRole ?? 'employee'
  if (wr === 'manager') return 'Manager'
  if (wr === 'team_leader') return 'Team Leader'
  return 'Employee'
}

function profilePathFor(role) {
  if (role === 'super_admin') return '/super-admin/profile'
  if (role === 'company_admin') return '/profile'
  if (role === 'sub_admin') return '/profile'
  if (role === 'user') return '/app/profile'
  return '/profile'
}
/**
 * User avatar menu — the person's own profile + sign out. Organisation
 * switching lives in the dedicated header pill (`OrganisationSwitcher`), so
 * this menu stays focused on the signed-in person.
 */
export function UserMenu({ user, onSignOut }) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)

  // "Take Product Tour" launcher shows for everyone except the Super Admin
  // console (where the tour isn't mounted).
  const isTourUser = user.role !== 'super_admin'

  useEffect(() => {
    if (!open) return undefined
    function onClick(e) {
      const target = e.target
      if (
        !menuRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-tour="tour-user-menu"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-3 rounded-full pl-4 pr-2 py-1 hover:bg-[color:var(--color-card-hover)] transition-colors cursor-pointer"
      >
        <div className="text-right hidden sm:block leading-tight">
          <p className="text-[color:var(--color-ink-strong)] font-semibold">
            {user.fullName}
          </p>
          <p className="text-sm text-[color:var(--color-accent-dim)] font-medium whitespace-nowrap">
            {displayRoleLabel(user)}
          </p>
        </div>
        <AccountAvatar
          name={user.fullName}
          avatarUrl={user.avatarUrl}
          size={36}
        />
        <ChevronDown
          size={14}
          className={`text-[color:var(--color-ink-muted)] transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          ref={menuRef}
          role="menu"
          className="absolute right-0 top-full mt-4 w-80 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-xl z-40 overflow-hidden"
        >
          <div className="p-4 border-b border-[color:var(--color-rule)] flex items-center gap-3">
            <AccountAvatar
              name={user.fullName}
              avatarUrl={user.avatarUrl}
              size={40}
            />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[color:var(--color-ink-strong)] truncate">
                {user.fullName}
              </p>
              <p className="text-sm text-[color:var(--color-ink-muted)] mt-0.5 truncate">
                {user.emailId}
              </p>
              {/* Role badge only — the active company name is NOT
               *  repeated here. It already sits immediately beside this
               *  menu in the OrganisationSwitcher pill (rendered
               *  unconditionally for tenant users), and showing it in
               *  both spots read as a duplicate (QA-reported). Super
               *  admins are tenantless and never had a company chip. */}
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-dim)] border border-[color:var(--color-accent-border)]">
                  {displayRoleLabel(user)}
                </span>
              </div>
            </div>
          </div>
          <nav className="p-2" role="none">
            <Link
              to={profilePathFor(user.role)}
              onClick={() => setOpen(false)}
              role="menuitem"
              className="flex items-center gap-3 px-3 py-2.5 rounded-md text-[color:var(--color-ink)] hover:bg-[color:var(--color-canvas)] cursor-pointer"
            >
              <User size={16} className="text-[color:var(--color-ink-muted)]" />
              Profile
            </Link>
            <Link
              to={preferencesPathFor(user.role)}
              onClick={() => setOpen(false)}
              role="menuitem"
              className="flex items-center gap-3 px-3 py-2.5 rounded-md text-[color:var(--color-ink)] hover:bg-[color:var(--color-canvas)] cursor-pointer"
            >
              <SlidersHorizontal size={16} className="text-[color:var(--color-ink-muted)]" />
              My Preferences
            </Link>
            {/* Start the guided product tour on demand. The tour only
             *  auto-opens on a user's FIRST login now, so this launcher is how
             *  a returning user (any panel) re-opens it. Closes the menu, then
             *  starts the tour imperatively. */}
            {isTourUser ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  startProductTour()
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[color:var(--color-ink)] hover:bg-[color:var(--color-canvas)] cursor-pointer"
              >
                <Compass size={16} className="text-[color:var(--color-ink-muted)]" />
                <span className="flex-1 text-left">Take Product Tour</span>
              </button>
            ) : null}
            <button
              type="button"
              onClick={async () => {
                setOpen(false)
                await onSignOut()
              }}
              role="menuitem"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[color:var(--color-ink)] hover:bg-[color:var(--color-canvas)] cursor-pointer"
            >
              <LogOut size={16} className="text-[color:var(--color-ink-muted)]" />
              Sign Out
            </button>
          </nav>
        </div>
      ) : null}
    </div>
  )
}
